import { FC, FormEvent, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Form,
  Modal,
  Pagination,
  Spinner,
  Table,
} from 'react-bootstrap';

type CrudResource = 'locations' | 'services' | 'providers';

type LocationOption = {
  id: number;
  code: string;
  display_name: string;
};

type PaginatedResponse<T> = {
  data: T[];
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
};

type ResourceIndexResponse<T> = {
  data: PaginatedResponse<T>;
  options?: {
    provider_types?: string[];
    provider_roles?: string[];
  };
};

type LocationRecord = {
  id: number;
  code: string;
  display_name: string;
  is_active: boolean;
  services?: Array<{ id: number; code: string; display_name: string }>;
};

type ServiceRecord = {
  id: number;
  code: string;
  display_name: string;
  is_active: boolean;
  locations?: LocationOption[];
};

type ProviderRecord = {
  id: number;
  full_name: string;
  abbreviation: string | null;
  provider_type: string | null;
  primary_role_title: string | null;
  email_address: string | null;
  clinical_fte: string | null;
  non_clinical_fte: string | null;
  fte: string | null;
  employee_number: string | null;
  phone_number: string | null;
  office_address: string | null;
  office_number: string | null;
  is_active: boolean;
  user?: {
    id: number;
    email: string;
  } | null;
};

type ResourceRecordMap = {
  locations: LocationRecord;
  services: ServiceRecord;
  providers: ProviderRecord;
};

type BaseFormState = {
  is_active: boolean;
};

type LocationFormState = BaseFormState & {
  code: string;
  display_name: string;
};

type ServiceFormState = BaseFormState & {
  code: string;
  display_name: string;
  location_ids: number[];
};

type ProviderFormState = BaseFormState & {
  full_name: string;
  abbreviation: string;
  provider_type: string;
  primary_role_title: string;
  email_address: string;
  clinical_fte: string;
  non_clinical_fte: string;
  fte: string;
  employee_number: string;
  phone_number: string;
  office_address: string;
  office_number: string;
};

type FormStateMap = {
  locations: LocationFormState;
  services: ServiceFormState;
  providers: ProviderFormState;
};

type ResourceCrudWorkspaceProps = {
  resource: CrudResource;
  tenantQuery: string;
};

type FieldErrors = Partial<Record<string, string>>;
type LoadState = 'loading' | 'ready' | 'error';

const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') ?? '';
const suggestPeopleCode = (fullName: string, records: ProviderRecord[]) => {
  const parts = fullName.trim().split(/[^A-Za-z]+/).filter(Boolean);
  if (parts.length < 2 || parts[parts.length - 1].length < 2) return '';
  const first = parts[0];
  const last = parts[parts.length - 1];
  const taken = new Set(records.map(record => record.abbreviation?.toLocaleUpperCase()).filter(Boolean));
  const letters = [...new Set(`${last}${first}`.split(''))];
  const candidates = letters.slice(1).map(letter => `${first[0].toLocaleUpperCase()}${last[0].toLocaleUpperCase()}${letter.toLocaleLowerCase()}`);
  candidates.push(`${first[0].toLocaleUpperCase()}${(first[1] ?? last[0]).toLocaleLowerCase()}${last[0].toLocaleUpperCase()}`);
  return candidates.find(code => code.length === 3 && !taken.has(code.toLocaleUpperCase())) ?? '';
};

const emptyFormState = {
  locations: {
    code: '',
    display_name: '',
    is_active: true,
  },
  services: {
    code: '',
    display_name: '',
    location_ids: [],
    is_active: true,
  },
  providers: {
    full_name: '',
    abbreviation: '',
    provider_type: 'Physician',
    primary_role_title: '',
    email_address: '',
    clinical_fte: '',
    non_clinical_fte: '',
    fte: '',
    employee_number: '',
    phone_number: '',
    office_address: '',
    office_number: '',
    is_active: true,
  },
} satisfies FormStateMap;

const workspaceCopy: Record<
  CrudResource,
  {
    title: string;
    description: string;
    searchPlaceholder: string;
    filteredEmptyLabel: string;
    filteredEmptyDescription: string;
    emptyLabel: string;
    emptyDescription: string;
    helperTitle: string;
    helperText: string;
    createLabel: string;
  }
> = {
  locations: {
    title: 'Location registry',
    description: 'List, add, and update schedule locations for the selected tenant.',
    searchPlaceholder: 'Search by name or code',
    filteredEmptyLabel: 'No locations match the current filter.',
    filteredEmptyDescription: 'Try a different name or code, or clear the search to see every location for this tenant.',
    emptyLabel: 'No locations have been created for this tenant yet.',
    emptyDescription: 'Create the first location so services and providers can be linked to a real care site.',
    helperTitle: 'Locations anchor service coverage and staffing setup.',
    helperText: 'Keep names clear and codes concise so imported worksheets and staff can reference the same place consistently.',
    createLabel: 'New location',
  },
  services: {
    title: 'Service registry',
    description: 'Manage service records and sync each service to one or more schedule locations.',
    searchPlaceholder: 'Search by name or code',
    filteredEmptyLabel: 'No services match the current filter.',
    filteredEmptyDescription: 'Try a different service name or code, or clear the search to review the full tenant service registry.',
    emptyLabel: 'No services have been created for this tenant yet.',
    emptyDescription: 'Create the first service and attach it to the locations it covers so staffing setup has the right service footprint.',
    helperTitle: 'Services should reflect real clinical coverage domains.',
    helperText: 'Attach each service to the locations it covers so readiness checks and future controls can rely on a stable relationship.',
    createLabel: 'New service',
  },
  providers: {
    title: 'People',
    description: 'Maintain the people available to the selected Schedule Group.',
    searchPlaceholder: 'Search by name, code, type, role, or email',
    filteredEmptyLabel: 'No people match the current filter.',
    filteredEmptyDescription: 'Try a different name, code, type, role, or email, or clear the search.',
    emptyLabel: 'No people have been added to this Schedule Group yet.',
    emptyDescription: 'Add the first person so eligibility, availability, and scheduling can be configured.',
    helperTitle: 'People are identities in the selected Schedule Group.',
    helperText: 'Eligibility and schedulability remain separate decisions.',
    createLabel: 'Add new',
  },
};

const ResourceCrudWorkspace: FC<ResourceCrudWorkspaceProps> = ({ resource, tenantQuery }) => {
  const [records, setRecords] = useState<ResourceRecordMap[typeof resource][]>([]);
  const [locations, setLocations] = useState<LocationOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [loadState, setLoadState] = useState<LoadState>('loading');
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formState, setFormState] = useState<FormStateMap[typeof resource]>(emptyFormState[resource]);
  const [reloadKey, setReloadKey] = useState(0);
  const [showProviderModal, setShowProviderModal] = useState(false);
  const [providerTypes, setProviderTypes] = useState(['Physician', 'APP', 'Nurse']);
  const [providerRoles, setProviderRoles] = useState(['ACSS Surgeon', 'EM-SCCS', 'General Surgeon', 'Locums', 'Other']);

  const copy = workspaceCopy[resource];

  useEffect(() => {
    setEditingId(null);
    setFormState(emptyFormState[resource]);
    setSuccess(null);
    setError(null);
    setLoadState('loading');
    setFieldErrors({});
    setPage(1);
    setSearchTerm('');
    setSearchInput('');
  }, [resource, tenantQuery]);

  useEffect(() => {
    let cancelled = false;

    const loadRecords = async () => {
      setLoading(true);
      setError(null);
      setLoadState('loading');

      try {
        const params = new URLSearchParams(tenantQuery);
        params.set('page', String(page));
        params.set('per_page', '10');
        if (searchTerm.trim() !== '') {
          params.set('search', searchTerm.trim());
        }

        const response = await fetch(`/api/v1/secure/schedule-settings/${resource}?${params.toString()}`, {
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error(`Unable to load ${resource} (${response.status}).`);
        }

        const payload = (await response.json()) as ResourceIndexResponse<ResourceRecordMap[typeof resource]>;

        if (!cancelled) {
          setRecords(payload.data.data);
          setLastPage(payload.data.last_page);
          setPage(payload.data.current_page);
          setTotal(payload.data.total);
          if (payload.options?.provider_types?.length) setProviderTypes(payload.options.provider_types);
          if (payload.options?.provider_roles?.length) setProviderRoles(payload.options.provider_roles);
          setLoadState('ready');
        }
      } catch (requestError) {
        if (!cancelled) {
          setError(requestError instanceof Error ? requestError.message : `Unable to load ${resource}.`);
          setLoadState('error');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void loadRecords();

    return () => {
      cancelled = true;
    };
  }, [page, reloadKey, resource, searchTerm, tenantQuery]);

  useEffect(() => {
    if (resource !== 'services') {
      return;
    }

    let cancelled = false;

    const loadLocations = async () => {
      try {
        const params = new URLSearchParams(tenantQuery);
        params.set('per_page', '100');

        const response = await fetch(`/api/v1/secure/schedule-settings/locations?${params.toString()}`, {
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error(`Unable to load locations (${response.status}).`);
        }

        const payload = (await response.json()) as ResourceIndexResponse<LocationRecord>;

        if (!cancelled) {
          setLocations(
            payload.data.data.map((record) => ({
              id: record.id,
              code: record.code,
              display_name: record.display_name,
            }))
          );
        }
      } catch (requestError) {
        if (!cancelled) {
          setError(requestError instanceof Error ? requestError.message : 'Unable to load locations.');
        }
      }
    };

    void loadLocations();

    return () => {
      cancelled = true;
    };
  }, [resource, tenantQuery]);

  const locationMap = useMemo(
    () =>
      new Map(
        locations.map((location) => [
          location.id,
          `${location.display_name} (${location.code})`,
        ])
      ),
    [locations]
  );

  const resetForm = () => {
    setEditingId(null);
    setFormState(emptyFormState[resource]);
    setFieldErrors({});
  };

  const handleSearchSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setPage(1);
    setSearchTerm(searchInput);
  };

  const beginEdit = (record: ResourceRecordMap[typeof resource]) => {
    setSuccess(null);
    setError(null);
    setFieldErrors({});
    setEditingId(record.id);

    if (resource === 'locations') {
      const location = record as LocationRecord;
      setFormState({
        code: location.code,
        display_name: location.display_name,
        is_active: location.is_active,
      } as FormStateMap[typeof resource]);

      return;
    }

    if (resource === 'services') {
      const service = record as ServiceRecord;
      setFormState({
        code: service.code,
        display_name: service.display_name,
        location_ids: service.locations?.map((location) => location.id) ?? [],
        is_active: service.is_active,
      } as FormStateMap[typeof resource]);

      return;
    }

    const provider = record as ProviderRecord;
    setFormState({
      full_name: provider.full_name,
      abbreviation: provider.abbreviation ?? '',
      provider_type: provider.provider_type ?? 'Physician',
      primary_role_title: provider.primary_role_title ?? '',
      email_address: provider.email_address ?? provider.user?.email ?? '',
      clinical_fte: provider.clinical_fte ?? '',
      non_clinical_fte: provider.non_clinical_fte ?? '',
      fte: provider.fte ?? '',
      employee_number: provider.employee_number ?? '',
      phone_number: provider.phone_number ?? '',
      office_address: provider.office_address ?? '',
      office_number: provider.office_number ?? '',
      is_active: provider.is_active,
    } as FormStateMap[typeof resource]);
    setShowProviderModal(true);
  };

  const updateFormState = <K extends keyof FormStateMap[typeof resource]>(
    key: K,
    value: FormStateMap[typeof resource][K]
  ) => {
    setFieldErrors((current) => {
      if (!(key in current)) {
        return current;
      }

      const nextErrors = { ...current };
      delete nextErrors[key as string];

      return nextErrors;
    });

    setFormState((current) => ({
      ...current,
      [key]: value,
    }));
  };

  const startCreate = () => {
    setSuccess(null);
    setError(null);
    resetForm();
    if (resource === 'providers') setShowProviderModal(true);
  };

  const updateProviderName = (fullName: string) => {
    setFieldErrors(current => ({ ...current, full_name: undefined }));
    setFormState(current => {
      const provider = current as ProviderFormState;
      return {
        ...provider,
        full_name: fullName,
        abbreviation: editingId === null && provider.abbreviation === '' ? suggestPeopleCode(fullName, records as ProviderRecord[]) : provider.abbreviation,
      } as FormStateMap[typeof resource];
    });
  };

  const validateForm = (): FieldErrors => {
    const errors: FieldErrors = {};

    if (resource === 'locations') {
      const locationState = formState as LocationFormState;

      if (locationState.code.trim() === '') {
        errors.code = 'Location code is required.';
      }

      if (locationState.display_name.trim() === '') {
        errors.display_name = 'Location name is required.';
      }
    }

    if (resource === 'services') {
      const serviceState = formState as ServiceFormState;

      if (serviceState.code.trim() === '') {
        errors.code = 'Service code is required.';
      }

      if (serviceState.display_name.trim() === '') {
        errors.display_name = 'Service name is required.';
      }

      if (serviceState.location_ids.length === 0) {
        errors.location_ids = 'Select at least one location for this service.';
      }
    }

    if (resource === 'providers') {
      const providerState = formState as ProviderFormState;

      if (providerState.full_name.trim() === '') {
        errors.full_name = 'Name is required.';
      }
      if (providerState.primary_role_title === '') errors.primary_role_title = 'Role is required.';
    }

    return errors;
  };

  const buildPayload = () => {
    if (resource === 'locations') {
      const locationState = formState as LocationFormState;

      return {
        code: locationState.code.trim(),
        display_name: locationState.display_name.trim(),
        is_active: locationState.is_active,
      };
    }

    if (resource === 'services') {
      const serviceState = formState as ServiceFormState;

      return {
        code: serviceState.code.trim(),
        display_name: serviceState.display_name.trim(),
        location_ids: serviceState.location_ids,
        is_active: serviceState.is_active,
      };
    }

    const providerState = formState as ProviderFormState;

    return {
      full_name: providerState.full_name.trim(),
      abbreviation: providerState.abbreviation.trim() || null,
      provider_type: providerState.provider_type,
      primary_role_title: providerState.primary_role_title,
      email_address: providerState.email_address.trim() || null,
      clinical_fte: providerState.clinical_fte === '' ? null : Number(providerState.clinical_fte),
      non_clinical_fte: providerState.non_clinical_fte === '' ? null : Number(providerState.non_clinical_fte),
      fte: providerState.fte === '' ? null : Number(providerState.fte),
      employee_number: providerState.employee_number.trim() || null,
      phone_number: providerState.phone_number.trim() || null,
      office_address: providerState.office_address.trim() || null,
      office_number: providerState.office_number.trim() || null,
      is_active: providerState.is_active,
    };
  };

  const refreshRecords = async () => {
    const params = new URLSearchParams(tenantQuery);
    params.set('page', String(page));
    params.set('per_page', '10');
    if (searchTerm.trim() !== '') {
      params.set('search', searchTerm.trim());
    }

    const response = await fetch(`/api/v1/secure/schedule-settings/${resource}?${params.toString()}`, {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Unable to refresh ${resource} (${response.status}).`);
    }

    const payload = (await response.json()) as ResourceIndexResponse<ResourceRecordMap[typeof resource]>;
    setRecords(payload.data.data);
    setLastPage(payload.data.last_page);
    setPage(payload.data.current_page);
    setTotal(payload.data.total);
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);
    setFieldErrors({});

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setFieldErrors(validationErrors);
      setSaving(false);

      return;
    }

    try {
      const params = new URLSearchParams(tenantQuery);
      const payload = buildPayload();
      const targetUrl =
        editingId === null
          ? `/api/v1/secure/schedule-settings/${resource}?${params.toString()}`
          : `/api/v1/secure/schedule-settings/${resource}/${editingId}?${params.toString()}`;

      const response = await fetch(targetUrl, {
        method: editingId === null ? 'POST' : 'PATCH',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': csrfToken,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const failure = (await response.json().catch(() => null)) as
          | { message?: string; errors?: Record<string, string[]> }
          | null;
        const fieldErrors = failure?.errors
          ? Object.values(failure.errors)
              .flat()
              .join(' ')
          : null;
        throw new Error(fieldErrors || failure?.message || `Save failed (${response.status}).`);
      }

      await refreshRecords();
      setSuccess(editingId === null ? 'Record created.' : 'Record updated.');
      resetForm();
      setShowProviderModal(false);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Unable to save record.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (record: ResourceRecordMap[typeof resource]) => {
    const resourceName =
      resource === 'locations' ? 'location' : resource === 'services' ? 'service' : 'provider';
    const recordName =
      resource === 'providers'
        ? (record as ProviderRecord).full_name
        : (record as LocationRecord | ServiceRecord).display_name;

    if (!window.confirm(`Delete ${recordName}? This will remove the ${resourceName} record for the active tenant.`)) {
      return;
    }

    setDeletingId(record.id);
    setError(null);
    setSuccess(null);

    try {
      const params = new URLSearchParams(tenantQuery);
      const response = await fetch(
        `/api/v1/secure/schedule-settings/${resource}/${record.id}?${params.toString()}`,
        {
          method: 'DELETE',
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
            'X-CSRF-TOKEN': csrfToken,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Delete failed (${response.status}).`);
      }

      if (editingId === record.id) {
        resetForm();
      }

      await refreshRecords();
      setSuccess(`${recordName} deleted.`);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Unable to delete record.');
    } finally {
      setDeletingId(null);
    }
  };

  const activeRecords = records.filter((record) => record.is_active).length;
  const inactiveRecords = Math.max(total - activeRecords, 0);
  const hasSearchFilter = searchTerm.trim() !== '';
  const isFilteredEmpty = !loading && loadState === 'ready' && records.length === 0 && hasSearchFilter;
  const isTrueEmpty = !loading && loadState === 'ready' && records.length === 0 && !hasSearchFilter;
  const showLoadErrorState = !loading && loadState === 'error' && records.length === 0;

  const clearSearch = () => {
    setSearchInput('');
    setSearchTerm('');
    setPage(1);
  };

  const renderRows = () => {
    if (records.length === 0) {
      return (
        <tr>
          <td colSpan={resource === 'providers' ? 8 : 4} className="text-center text-muted py-4">
            {copy.emptyLabel}
          </td>
        </tr>
      );
    }

    if (resource === 'locations') {
      return (records as LocationRecord[]).map((record) => (
        <tr key={record.id}>
          <td>
            <div className="fw-semibold">{record.display_name}</div>
            <div className="text-muted fs-12">{record.code}</div>
          </td>
          <td>{record.services?.length ?? 0}</td>
          <td>
            <Badge bg="" className={record.is_active ? 'bg-success-transparent text-success' : 'bg-light text-muted'}>
              {record.is_active ? 'Active' : 'Inactive'}
            </Badge>
          </td>
          <td className="text-end">
            <Button size="sm" variant="primary-light" onClick={() => beginEdit(record)}>
              Edit
            </Button>
            <Button
              size="sm"
              variant="danger-light"
              className="ms-2"
              disabled={deletingId === record.id}
              onClick={() => void handleDelete(record)}
            >
              {deletingId === record.id ? 'Deleting…' : 'Delete'}
            </Button>
          </td>
        </tr>
      ));
    }

    if (resource === 'services') {
      return (records as ServiceRecord[]).map((record) => (
        <tr key={record.id}>
          <td>
            <div className="fw-semibold">{record.display_name}</div>
            <div className="text-muted fs-12">{record.code}</div>
          </td>
          <td>
            <div className="d-flex flex-wrap gap-1">
              {record.locations?.length ? (
                record.locations.map((location) => (
                  <Badge key={location.id} bg="" className="bg-info-transparent text-info">
                    {location.code}
                  </Badge>
                ))
              ) : (
                <span className="text-muted fs-12">No linked locations</span>
              )}
            </div>
          </td>
          <td>
            <Badge bg="" className={record.is_active ? 'bg-success-transparent text-success' : 'bg-light text-muted'}>
              {record.is_active ? 'Active' : 'Inactive'}
            </Badge>
          </td>
          <td className="text-end">
            <Button size="sm" variant="primary-light" onClick={() => beginEdit(record)}>
              Edit
            </Button>
            <Button
              size="sm"
              variant="danger-light"
              className="ms-2"
              disabled={deletingId === record.id}
              onClick={() => void handleDelete(record)}
            >
              {deletingId === record.id ? 'Deleting…' : 'Delete'}
            </Button>
          </td>
        </tr>
      ));
    }

    return (records as ProviderRecord[]).map((record) => (
      <tr key={record.id}>
        <td className="fw-semibold">{record.abbreviation ?? '—'}</td>
        <td>{record.full_name}</td>
        <td>{record.provider_type ?? '—'}</td>
        <td>{record.primary_role_title ?? '—'}</td>
        <td>{record.clinical_fte ?? '—'}</td>
        <td className="text-break">{record.email_address ?? record.user?.email ?? '—'}</td>
        <td>
          <Badge bg="" className={record.is_active ? 'bg-success-transparent text-success' : 'bg-light text-muted'}>
            {record.is_active ? 'Active' : 'Inactive'}
          </Badge>
        </td>
        <td className="text-end">
          <Button size="sm" variant="primary-light" onClick={() => beginEdit(record)}>
            Edit
          </Button>
          <Button
            size="sm"
            variant="danger-light"
            className="ms-2"
            disabled={deletingId === record.id}
            onClick={() => void handleDelete(record)}
          >
            {deletingId === record.id ? 'Deleting…' : 'Delete'}
          </Button>
        </td>
      </tr>
    ));
  };

  return (
    <Card className="custom-card h-100">
      <Card.Header className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-3">
        <div>
          <div className="card-title mb-1">{copy.title}</div>
          <p className="text-muted fs-13 mb-0">{copy.description}</p>
        </div>
        <div className="d-flex flex-wrap gap-2">
          <Badge bg="" className="bg-primary-transparent text-primary">
            {total} records
          </Badge>
          <Badge bg="" className="bg-success-transparent text-success">
            {activeRecords} active
          </Badge>
          <Badge bg="" className="bg-light text-muted">
            {inactiveRecords} inactive
          </Badge>
        </div>
      </Card.Header>
      <Card.Body>
        {error && (
          <Alert variant="warning" className="mb-4">
            {error}
          </Alert>
        )}

        {success && (
          <Alert variant="success" className="mb-4">
            {success}
          </Alert>
        )}

        {resource !== 'providers' && <div className="rounded-3 border bg-light p-3 p-lg-4 mb-4">
          <div className="d-flex flex-column flex-lg-row align-items-lg-start justify-content-between gap-3 mb-3">
            <div>
              <h6 className="mb-1">{editingId === null ? `Create ${copy.createLabel.toLowerCase()}` : 'Edit existing record'}</h6>
              <p className="text-muted fs-13 mb-0">
                {copy.helperTitle} {copy.helperText}
              </p>
            </div>
            <div className="d-flex flex-wrap gap-2">
              {editingId !== null && (
                <Badge bg="" className="bg-warning-transparent text-warning">
                  Editing existing record
                </Badge>
              )}
              <Button variant="primary-light" size="sm" onClick={startCreate}>
                {copy.createLabel}
              </Button>
            </div>
          </div>

          <Form onSubmit={handleSubmit} noValidate>
            {resource !== 'providers' && (
              <div className="row g-3">
                <div className="col-md-4">
                  <Form.Group controlId={`${resource}-code`}>
                    <Form.Label>Code</Form.Label>
                    <Form.Control
                      value={(formState as LocationFormState | ServiceFormState).code}
                      onChange={(event) => updateFormState('code', event.target.value as never)}
                      isInvalid={fieldErrors.code !== undefined}
                      required
                    />
                    <Form.Text className="text-muted">Codes are normalized to uppercase on save.</Form.Text>
                    <Form.Control.Feedback type="invalid">{fieldErrors.code}</Form.Control.Feedback>
                  </Form.Group>
                </div>
                <div className="col-md-5">
                  <Form.Group controlId={`${resource}-name`}>
                    <Form.Label>Name</Form.Label>
                    <Form.Control
                      value={(formState as LocationFormState | ServiceFormState).display_name}
                      onChange={(event) => updateFormState('display_name', event.target.value as never)}
                      isInvalid={fieldErrors.display_name !== undefined}
                      required
                    />
                    <Form.Control.Feedback type="invalid">{fieldErrors.display_name}</Form.Control.Feedback>
                  </Form.Group>
                </div>
                <div className="col-md-3 d-flex align-items-end">
                  <Form.Check
                    id={`${resource}-active`}
                    type="switch"
                    label="Active"
                    checked={formState.is_active}
                    onChange={(event) => updateFormState('is_active', event.target.checked as never)}
                  />
                </div>
              </div>
            )}

            {resource === 'services' && (
              <div className="mt-3">
                <Form.Group controlId="services-locations">
                  <Form.Label>Locations</Form.Label>
                  <Form.Select
                    multiple
                    value={(formState as ServiceFormState).location_ids.map(String)}
                    isInvalid={fieldErrors.location_ids !== undefined}
                    onChange={(event) =>
                      updateFormState(
                        'location_ids',
                        Array.from(event.target.selectedOptions).map((option) => Number(option.value)) as never
                      )
                    }
                  >
                    {locations.map((location) => (
                      <option key={location.id} value={location.id}>
                        {location.display_name} ({location.code})
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Hold Ctrl/Cmd to select multiple locations for a single service.
                  </Form.Text>
                  <Form.Control.Feedback type="invalid">{fieldErrors.location_ids}</Form.Control.Feedback>
                </Form.Group>
              </div>
            )}

            {resource === 'services' && (formState as ServiceFormState).location_ids.length > 0 && (
              <div className="mt-3 d-flex flex-wrap gap-2">
                {(formState as ServiceFormState).location_ids.map((locationId) => (
                  <Badge key={locationId} bg="" className="bg-secondary-transparent text-secondary">
                    {locationMap.get(locationId) ?? `Location ${locationId}`}
                  </Badge>
                ))}
              </div>
            )}

            <div className="d-flex flex-wrap gap-2 mt-4">
              <Button type="submit" disabled={saving}>
                {saving ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Saving
                  </>
                ) : editingId === null ? (
                  'Save new record'
                ) : (
                  'Save changes'
                )}
              </Button>
              <Button type="button" variant="light" onClick={resetForm}>
                {editingId === null ? 'Clear form' : 'Cancel edit'}
              </Button>
            </div>
          </Form>
        </div>}

        <hr className="my-4" />

        <div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-3 mb-3">
          <div>
            <h6 className="mb-1">{resource === 'providers' ? 'People' : 'Saved records'}</h6>
            <p className="text-muted fs-13 mb-0">
              Search the current tenant set, then edit or remove records directly from the table.
            </p>
          </div>
          <Form onSubmit={handleSearchSubmit} className="d-flex flex-column flex-sm-row gap-2">
            <Form.Control
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder={copy.searchPlaceholder}
            />
            <Button type="submit" variant="primary-light">
              Search
            </Button>
            {resource === 'providers' && <Button type="button" variant="primary" onClick={startCreate}>Add new</Button>}
            {hasSearchFilter && (
              <Button
                type="button"
                variant="light"
                onClick={clearSearch}
              >
                Clear
              </Button>
            )}
          </Form>
        </div>

        {showLoadErrorState ? (
          <div className="rounded-3 border border-warning-subtle bg-warning-transparent text-center p-4 p-lg-5">
            <span className="avatar avatar-xl bg-warning-transparent text-warning mb-3">
              <i className="ri-error-warning-line fs-24"></i>
            </span>
            <h6 className="mb-2">Unable to load saved records</h6>
            <p className="text-muted mb-3">
              {error ?? `The ${copy.title.toLowerCase()} could not be loaded for this tenant. Try the request again.`}
            </p>
            <Button variant="warning" onClick={() => setReloadKey((current) => current + 1)}>
              Retry load
            </Button>
          </div>
        ) : isFilteredEmpty ? (
          <div className="rounded-3 border border-dashed text-center p-4 p-lg-5">
            <span className="avatar avatar-xl bg-info-transparent text-info mb-3">
              <i className="ri-search-line fs-24"></i>
            </span>
            <h6 className="mb-2">{copy.filteredEmptyLabel}</h6>
            <p className="text-muted mb-3">{copy.filteredEmptyDescription}</p>
            <Button variant="primary-light" onClick={clearSearch}>
              Clear search
            </Button>
          </div>
        ) : isTrueEmpty ? (
          <div className="rounded-3 border border-dashed text-center p-4 p-lg-5">
            <span className="avatar avatar-xl bg-primary-transparent text-primary mb-3">
              <i className="ri-play-list-add-line fs-24"></i>
            </span>
            <h6 className="mb-2">{copy.emptyLabel}</h6>
            <p className="text-muted mb-3">{copy.emptyDescription}</p>
            <Button variant="primary" onClick={startCreate}>
              {copy.createLabel}
            </Button>
          </div>
        ) : (
          <div className="table-responsive">
            <Table hover className={`align-middle mb-3${resource === 'providers' ? ' schedule-people-table' : ' text-nowrap'}`}>
              <thead>
                {resource === 'providers' ? <tr><th>Code</th><th>Name</th><th>Type</th><th>Role</th><th>Clinical FTE</th><th>Email address</th><th>Status</th><th className="text-end">Actions</th></tr> : <tr>
                  <th>{resource === 'providers' ? 'Provider' : resource === 'services' ? 'Service' : 'Location'}</th>
                  <th>{resource === 'providers' ? 'Email' : resource === 'services' ? 'Locations' : 'Linked services'}</th>
                  <th>Status</th>
                  <th className="text-end">Actions</th>
                </tr>}
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={resource === 'providers' ? 8 : 4} className="text-center py-4">
                      <Spinner animation="border" size="sm" className="me-2" />
                      Loading records
                    </td>
                  </tr>
                ) : (
                  renderRows()
                )}
              </tbody>
            </Table>
          </div>
        )}

        {lastPage > 1 && (
          <Pagination className="mb-0">
            <Pagination.Prev disabled={page <= 1 || loading} onClick={() => setPage((current) => current - 1)} />
            <Pagination.Item active>{page}</Pagination.Item>
            <Pagination.Next disabled={page >= lastPage || loading} onClick={() => setPage((current) => current + 1)} />
          </Pagination>
        )}

        <Modal show={resource === 'providers' && showProviderModal} onHide={() => { setShowProviderModal(false); resetForm(); }} size="lg" centered>
          <Form onSubmit={handleSubmit} noValidate>
            <Modal.Header closeButton><Modal.Title>{editingId === null ? 'Add new person' : 'Edit person'}</Modal.Title></Modal.Header>
            <Modal.Body>
              <div className="row g-3">
                <div className="col-md-8"><Form.Group><Form.Label>Name</Form.Label><Form.Control value={(formState as ProviderFormState).full_name} onChange={event => updateProviderName(event.target.value)} isInvalid={fieldErrors.full_name !== undefined} autoFocus /><Form.Control.Feedback type="invalid">{fieldErrors.full_name}</Form.Control.Feedback></Form.Group></div>
                <div className="col-md-4"><Form.Group><Form.Label>Code</Form.Label><Form.Control maxLength={3} value={(formState as ProviderFormState).abbreviation} onChange={event => updateFormState('abbreviation', event.target.value as never)} isInvalid={fieldErrors.abbreviation !== undefined} /><Form.Text>Three unique letters are suggested from the name.</Form.Text><Form.Control.Feedback type="invalid">{fieldErrors.abbreviation}</Form.Control.Feedback></Form.Group></div>
                <div className="col-md-4"><Form.Group><Form.Label>Type</Form.Label><Form.Select value={(formState as ProviderFormState).provider_type} onChange={event => updateFormState('provider_type', event.target.value as never)}>{providerTypes.map(option => <option key={option}>{option}</option>)}</Form.Select></Form.Group></div>
                <div className="col-md-8"><Form.Group><Form.Label>Role</Form.Label><Form.Select value={(formState as ProviderFormState).primary_role_title} onChange={event => updateFormState('primary_role_title', event.target.value as never)} isInvalid={fieldErrors.primary_role_title !== undefined}><option value="">Select a role…</option>{providerRoles.map(option => <option key={option}>{option}</option>)}</Form.Select><Form.Text>Role choices are governed by the Eligibility setup.</Form.Text><Form.Control.Feedback type="invalid">{fieldErrors.primary_role_title}</Form.Control.Feedback></Form.Group></div>
                <div className="col-md-6"><Form.Group><Form.Label>Email address</Form.Label><Form.Control type="email" value={(formState as ProviderFormState).email_address} onChange={event => updateFormState('email_address', event.target.value as never)} /></Form.Group></div>
                <div className="col-md-2"><Form.Group><Form.Label>Clinical FTE</Form.Label><Form.Control type="number" min="0" max="1" step="0.01" value={(formState as ProviderFormState).clinical_fte} onChange={event => updateFormState('clinical_fte', event.target.value as never)} /></Form.Group></div>
                <div className="col-md-2"><Form.Group><Form.Label>Non-clinical FTE</Form.Label><Form.Control type="number" min="0" max="1" step="0.01" value={(formState as ProviderFormState).non_clinical_fte} onChange={event => updateFormState('non_clinical_fte', event.target.value as never)} /></Form.Group></div>
                <div className="col-md-2"><Form.Group><Form.Label>Total FTE</Form.Label><Form.Control type="number" min="0" step="0.01" value={(formState as ProviderFormState).fte} onChange={event => updateFormState('fte', event.target.value as never)} /></Form.Group></div>
                <div className="col-md-4"><Form.Group><Form.Label>Employee number</Form.Label><Form.Control value={(formState as ProviderFormState).employee_number} onChange={event => updateFormState('employee_number', event.target.value as never)} /></Form.Group></div>
                <div className="col-md-4"><Form.Group><Form.Label>Phone number</Form.Label><Form.Control type="tel" value={(formState as ProviderFormState).phone_number} onChange={event => updateFormState('phone_number', event.target.value as never)} /></Form.Group></div>
                <div className="col-md-4"><Form.Group><Form.Label>Office number</Form.Label><Form.Control value={(formState as ProviderFormState).office_number} onChange={event => updateFormState('office_number', event.target.value as never)} /></Form.Group></div>
                <div className="col-md-9"><Form.Group><Form.Label>Office address</Form.Label><Form.Control value={(formState as ProviderFormState).office_address} onChange={event => updateFormState('office_address', event.target.value as never)} /></Form.Group></div>
                <div className="col-md-3 d-flex align-items-end"><Form.Check type="switch" label="Active" checked={formState.is_active} onChange={event => updateFormState('is_active', event.target.checked as never)} /></div>
              </div>
            </Modal.Body>
            <Modal.Footer><Button type="button" variant="light" onClick={() => { setShowProviderModal(false); resetForm(); }}>Cancel</Button><Button type="submit" disabled={saving}>{saving ? 'Saving…' : editingId === null ? 'Add person' : 'Save changes'}</Button></Modal.Footer>
          </Form>
        </Modal>
      </Card.Body>
    </Card>
  );
};

export default ResourceCrudWorkspace;
